import {
  GlobalSearchScopeKind,
  GlobalSearchValueMatchInterface,
  GlobalSearchValueSource,
  GroupInterface,
  LocationInterface,
  LocationVarKey
} from "@swivl-mono/types";

import { findDetailsV3SubpageForVarKey } from "./GlobalSearch.DetailsV3Index";
import { SearchEntry, SearchResultCategory } from "./GlobalSearch.Types";
import { findFieldLabelForVarKey } from "./GlobalSearch.VarKeyLabels";

const EMERGENCY_SLUG = "emergency";

const ROUTING_GROUP_FIELD_LABELS: Record<string, string> = {
  name: "Group Name",
  description: "Description",
  escalationForwardPhoneNumber: "Escalation Phone",
  outsideHoursForwardPhoneNumber: "After-Hours Phone",
  alertSmsRecipients: "Alert SMS Recipients",
  alertEmailRecipients: "Alert Email Recipients"
};

function scopeName(match: GlobalSearchValueMatchInterface, orgName: string, groups: GroupInterface[], locations: LocationInterface[]): string {
  if (match.scopeKind === GlobalSearchScopeKind.Location) return locations.find((l) => l.id === match.locationId)?.name ?? "";
  if (match.scopeKind === GlobalSearchScopeKind.Group) return groups.find((g) => g.id === match.groupId)?.name ?? "";
  return orgName;
}

// "Subpage · Field · Scope" — the "(group)"/"(org)" qualifier is only useful
// when it's not the plain location scope the user's likely already viewing.
function breadcrumbFor(
  topLabel: string,
  fieldLabel: string | undefined,
  match: GlobalSearchValueMatchInterface,
  orgName: string,
  groups: GroupInterface[],
  locations: LocationInterface[]
): string {
  const scope = scopeName(match, orgName, groups, locations);
  const scopeText = match.scopeKind === GlobalSearchScopeKind.Location ? scope : `${scope} (${match.scopeKind})`;
  return [topLabel, fieldLabel, scopeText].filter(Boolean).join(" · ");
}

// Converts backend value matches into the same SearchEntry shape label matches use.
export function buildValueSearchEntries(
  matches: GlobalSearchValueMatchInterface[],
  orgName: string,
  groups: GroupInterface[],
  locations: LocationInterface[],
  isStaff: boolean
): SearchEntry[] {
  const entries: SearchEntry[] = [];

  matches.forEach((match, index) => {
    const scope = { groupId: match.groupId, locationId: match.locationId };
    const matchText = match.value.toLowerCase();

    if (match.source === GlobalSearchValueSource.LocationVariable) {
      const subpage = findDetailsV3SubpageForVarKey(match.locationVarKey as LocationVarKey);
      if (!subpage) return; // Var not surfaced on any Details v3 page — nowhere to link to.
      if (subpage.swivlStaffOnly && !isStaff) return;
      const fieldLabel = findFieldLabelForVarKey(match.locationVarKey as LocationVarKey);
      entries.push({
        id: `value:location-variable:${index}`,
        category: SearchResultCategory.DETAILS_V3 as const,
        title: match.value,
        subpage: subpage.to,
        breadcrumb: breadcrumbFor(subpage.label, fieldLabel, match, orgName, groups, locations),
        matchText,
        scope
      });
    } else if (match.source === GlobalSearchValueSource.RoutingGroup) {
      const subpage =
        match.routingGroupSlug === EMERGENCY_SLUG
          ? { to: "emergency", label: "Emergency" }
          : { to: "escalation-and-ticketing", label: "Escalation & Ticketing" };
      const fieldLabel = match.routingGroupField ? ROUTING_GROUP_FIELD_LABELS[match.routingGroupField] : undefined;
      entries.push({
        id: `value:routing-group:${index}`,
        category: SearchResultCategory.DETAILS_V3 as const,
        title: match.value,
        subpage: subpage.to,
        breadcrumb: breadcrumbFor(subpage.label, fieldLabel, match, orgName, groups, locations),
        matchText,
        scope
      });
    } else if (match.source === GlobalSearchValueSource.PolicyWorksheet && match.policyKey) {
      entries.push({
        id: `value:policy-worksheet:${index}`,
        category: SearchResultCategory.POLICY as const,
        title: match.value,
        policyKey: match.policyKey,
        breadcrumb: breadcrumbFor(match.policyTitle ?? match.policyKey, match.policyItemLabel, match, orgName, groups, locations),
        matchText,
        scope
      });
    }
  });

  return entries;
}
